import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { db } from '../lib/firebase.js';
import { collection, query, where, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { Box, Typography, CircularProgress, Paper, Button, Grid, Card, CardContent, CardActions, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, TextField, InputAdornment, Chip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { VolumeUp as VolumeUpIcon, Share as ShareIcon, Search as SearchIcon, AutoStories as AutoStoriesIcon, Add as AddIcon, ViewModule as ViewModuleIcon, ViewList as ViewListIcon } from '@mui/icons-material';
import { Trash2 } from 'lucide-react';
import ShareDialog from '../components/ShareDialog/ShareDialog.js';
import { motion } from 'framer-motion';
import { useSubscription } from '../contexts/SubscriptionContext.js';
import * as toastHook from '../hooks/use-toast.jsx';
import StoryShelf from '../components/StoryShelf/StoryShelf.js';

const DEFAULT_COVER_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

const getPreviewText = (story) => {
  const text = story.content || story.pages?.[0]?.content || '';
  if (!text) return null;
  const trimmed = text.trim().replace(/\s+/g, ' ');
  return trimmed.length > 90 ? trimmed.slice(0, 90) + '…' : trimmed;
};

const getWordCount = (story) => {
  const text = story.content || story.pages?.map(p => p.content).join(' ') || '';
  return text.trim().split(/\s+/).filter(Boolean).length;
};

function StoryLibraryPage() {
  const { user, isSignedIn } = useUser();
  const { tier, limits } = useSubscription();
  const { toast } = toastHook.useToast();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [storyToShare, setStoryToShare] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    try {
      return window.localStorage.getItem('make-it-mindful-library-view') || 'shelf';
    } catch {
      return 'shelf';
    }
  });
  const [selectedStoryId, setSelectedStoryId] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    const fetchStories = async () => {
      if (!user) {
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const q = query(
          collection(db, 'stories'),
          where('user_id', '==', user.id)
        );
        const querySnapshot = await getDocs(q);
        const accountAuthorName = user.fullName || user.username || 'Make It Mindful';
        const data = querySnapshot.docs
          .map(doc => ({ id: doc.id, author_name: doc.data().author_name || accountAuthorName, ...doc.data() }))
          .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));

        setStories(data || []);
      } catch (err) {
        setError(`Failed to load stories: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchStories();
  }, [user, retryKey]);

  // Filter stories based on search query
  const filteredStories = stories.filter(story =>
    story.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    story.setting?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (!filteredStories.some((story) => story.id === selectedStoryId)) {
      setSelectedStoryId(filteredStories[0]?.id || null);
    }
  }, [filteredStories, selectedStoryId]);

  const selectedStory = filteredStories.find((story) => story.id === selectedStoryId) || filteredStories[0] || null;

  const handleViewModeChange = (event, nextMode) => {
    if (!nextMode) return;
    setViewMode(nextMode);
    try {
      window.localStorage.setItem('make-it-mindful-library-view', nextMode);
    } catch {
      // View preference is optional; the in-memory state is enough.
    }
  };

  const handleDeleteClick = (story) => {
    setStoryToDelete(story);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!storyToDelete) return;

    try {
      const storyRef = doc(db, 'stories', storyToDelete.id);
      await deleteDoc(storyRef);
      setStories(stories.filter(story => story.id !== storyToDelete.id));
      setDeleteDialogOpen(false);
      setStoryToDelete(null);
      toast({ title: 'Story deleted', description: `"${storyToDelete.title}" was removed from your library.` });
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, variant: 'destructive' });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setStoryToDelete(null);
  };

  const handleOpenShareDialog = async (story) => {
    try {
      if (story.is_public !== true) {
        await updateDoc(doc(db, 'stories', story.id), { is_public: true });
        story = { ...story, is_public: true };
        setStories((previous) => previous.map((item) => item.id === story.id ? story : item));
      }
      setStoryToShare(story);
      setShareDialogOpen(true);
    } catch (err) {
      toast({ title: 'Sharing unavailable', description: 'Could not enable sharing for this story.', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4, px: 2 }}>
        <Typography color="error" role="alert">{error}</Typography>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setRetryKey((value) => value + 1)}>
          Try again
        </Button>
      </Box>
    );
  }

  if (!isSignedIn) {
    return (
       <Box sx={{ textAlign: 'center', mt: 4 }}>
         <Typography>Please log in to view your story library.</Typography>
       </Box>
     );
  }

  const now = new Date();
  const storiesThisMonth = stories.filter(s => {
    const d = new Date(s.created_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const storyLimit = limits?.storiesPerMonth;
  const limitLabel = storyLimit === Infinity ? 'Unlimited' : `${storiesThisMonth} / ${storyLimit} this month`;

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', my: 4, px: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, mb: 2, mt: 8, gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoStoriesIcon sx={{ fontSize: 36 }} />
            My Storybooks
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {limitLabel} stories
          </Typography>
        </Box>
        <Button
          component={RouterLink}
          to="/wizard"
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ borderRadius: 2, alignSelf: { xs: 'stretch', sm: 'auto' } }}
        >
          Create New Story
        </Button>
      </Box>

      {/* Search Bar */}
      {stories.length > 0 && (
        <Box sx={{ mb: 4, display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            placeholder="Search your stories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%',
              maxWidth: 400,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            aria-label="Library view"
            sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' } }}
          >
            <ToggleButton value="shelf" aria-label="Shelf view">
              <ViewModuleIcon fontSize="small" />
              <Typography component="span" sx={{ ml: 0.75, display: { xs: 'none', md: 'inline' } }}>Shelf</Typography>
            </ToggleButton>
            <ToggleButton value="grid" aria-label="Grid view">
              <ViewListIcon fontSize="small" />
              <Typography component="span" sx={{ ml: 0.75, display: { xs: 'none', md: 'inline' } }}>Grid</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {stories.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <AutoStoriesIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Your library is empty
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Create your first storybook to start building your collection!
          </Typography>
          <Button
            component={RouterLink}
            to="/wizard"
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
          >
            Create Your First Story
          </Button>
        </Paper>
      ) : filteredStories.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">
            No stories found matching "{searchQuery}"
          </Typography>
        </Paper>
      ) : viewMode === 'shelf' ? (
        <StoryShelf
          stories={filteredStories}
          selectedStory={selectedStory}
          onSelect={(story) => setSelectedStoryId(story.id)}
          onShare={handleOpenShareDialog}
          onDelete={handleDeleteClick}
        />
      ) : (
        <Grid container spacing={3}>
          {filteredStories.map((story, index) => {
            const hasPages = story.pages && Array.isArray(story.pages) && story.pages.length > 0;
            const pageCount = hasPages ? story.pages.length : 0;
            const previewText = getPreviewText(story);
            const wordCount = getWordCount(story);
            const readingMins = Math.max(1, Math.round(wordCount / 180));
            const hasStoryAudio = story.audio_urls && Object.keys(story.audio_urls).length > 0;
            const hasPageAudio = story.audio_page_urls && Object.values(story.audio_page_urls).some(
              (voicePages) => voicePages && Object.keys(voicePages).length > 0
            );
            const hasAudio = hasStoryAudio || hasPageAudio;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={story.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  style={{ height: '100%' }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    {/* Cover Image */}
                    <Box
                      component={RouterLink}
                      to={`/story/${story.id}`}
                      sx={{
                        height: 180,
                        background: story.cover_image_url
                          ? `url(${story.cover_image_url}) center/cover`
                          : DEFAULT_COVER_GRADIENT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        textDecoration: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {!story.cover_image_url && (
                        <AutoStoriesIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.8)' }} />
                      )}
                      {pageCount > 0 && (
                        <Chip
                          label={`${pageCount} pages`}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                      {hasAudio && (
                        <Chip
                          icon={<VolumeUpIcon sx={{ fontSize: '0.85rem !important', color: 'white !important' }} />}
                          label="Audio"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            bgcolor: 'rgba(123,104,238,0.85)',
                            color: 'white',
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </Box>

                    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                      <Typography
                        variant="subtitle1"
                        component="h2"
                        sx={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mb: 0.5,
                        }}
                      >
                        {story.title || 'Untitled Story'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(story.created_at).toLocaleDateString()}
                        </Typography>
                        {wordCount > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            · {readingMins} min read
                          </Typography>
                        )}
                      </Box>
                      {previewText && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5, lineHeight: 1.4, fontStyle: 'italic' }}
                        >
                          {previewText}
                        </Typography>
                      )}
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'space-between', pt: 0, px: 2, pb: 2 }}>
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/story/${story.id}`}
                        variant="contained"
                        sx={{ borderRadius: 1 }}
                      >
                        Read
                      </Button>
                      <Box>
                        <Tooltip title="Share">
                          <IconButton
                            onClick={() => handleOpenShareDialog(story)}
                            size="small"
                          >
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDeleteClick(story)}
                            size="small"
                            color="error"
                          >
                            <Trash2 size={18} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Story</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{storyToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        title={storyToShare?.title || 'Shared Story'}
        storyUrl={storyToShare ? `${window.location.origin}/story/${storyToShare.id}` : ''}
        isOwnStory={true}
      />
    </Box>
  );
}

export default StoryLibraryPage;
